import Catalogs from './catalogs';
import Handlebars from 'handlebars';

class Tabs {
  constructor() {
    this.container = document.querySelector('.js-nlc-maps-tabs');
    this.catalogs = new Catalogs();
    this.searchTimeout = null;
    this.currentSearch = '';
    this.currentPage = 1;
    this.totalPages = 1;
    this.totalSize = 0;

    // Tree browsing state
    this.currentPath = '';
    this.basePath = ''; // auto-skipped prefix (server IP, archiv etc.) - hidden from breadcrumbs
    this.mode = 'browse'; // 'browse' or 'search'

    // Cache of exact bounds fetched on-demand (used in tree mode)
    this.exactBoundsCache = {};

    this.generateTabsAndCheckboxes();
  }

  generateTabsAndCheckboxes() {
    if (!this.container) return;

    this.registerHandlebars();

    // New flow: tiles are loaded only after the user draws a polygon.
    // Show a hint until then.
    this.renderEmpty(
      nlc_map_settings.hint_draw_polygon_first ||
        nlc_map_settings.hint_draw_polygon ||
        'Draw a polygon on the map to load relevant tiles'
    );
  }

  // Show a simple message in the wrapper (used for empty / hint / error states).
  renderEmpty(message) {
    const wrapper = this.ensureWrapper();
    const newWrapper = wrapper.cloneNode(false);
    newWrapper.innerHTML = `<div class="nlc-maps-tabs__empty">${this.escapeHtml(message)}</div>`;
    wrapper.parentNode.replaceChild(newWrapper, wrapper);
  }

  // Fetch tiles for a polygon and render them grouped by year and category.
  // Called from map.js whenever the user finishes drawing or selecting an area.
  // Auto-streams the full result if the first batch is truncated.
  async loadByPolygon(geojsonPolygon, options = {}) {
    if (!geojsonPolygon) return;

    // Cancel any background streaming for a previous polygon
    if (this._streamAbort) {
      this._streamAbort.abort();
      this._streamAbort = null;
    }
    this._streaming = false;

    this.container.classList.add('loading');
    this._lastPolygon = geojsonPolygon;
    const PAGE_SIZE = 500;

    // Track the polygon Apollo actually accepted (may be the bbox fallback,
    // not the original outline). Streaming must reuse it for consistent paging.
    let effectivePolygon = geojsonPolygon;

    try {
      let data = await this.catalogs.getTilesByPolygon(effectivePolygon, {
        signal: options.signal,
        limit: PAGE_SIZE,
        offset: 0,
      });

      // If Apollo rejected the polygon as invalid (typically self-intersection
      // produced by simplification of complex boundaries like Bratislavský +
      // Danube), retry with the polygon's bounding box. Slightly looser area
      // but always valid.
      if (data === null && this._lastGetTilesByPolygonError === 'invalid-polygon') {
        const bboxPolygon = this.polygonToBboxGeoJSON(geojsonPolygon);
        if (bboxPolygon) {
          console.warn('[nlc-maps] retrying with bounding box after invalid polygon error');
          effectivePolygon = bboxPolygon;
          data = await this.catalogs.getTilesByPolygon(effectivePolygon, {
            signal: options.signal,
            limit: PAGE_SIZE,
            offset: 0,
          });
        }
      }

      if (data === null) {
        this.container.classList.remove('loading');
        return;
      }
      this._lastData = data;
      this._lastPolygon = effectivePolygon;

      // Mark streaming BEFORE first render so the banner shows "loading X/Y…"
      const needsStreaming =
        data.truncated && data.totalSize > data.returnedSize;
      if (needsStreaming) this._streaming = true;

      this.applyCurrentSearch();

      if (needsStreaming) {
        // Stream remaining pages using the polygon Apollo actually accepted.
        this.streamRemainingTiles(effectivePolygon, data, PAGE_SIZE);
      } else {
        // Single-page result — prune now, no later opportunity. (Streaming
        // path prunes at the end of streamRemainingTiles instead.)
        this.pruneObsoleteSelections();
        // No more pages to wait on → drop the carry-forward set.
        this._pendingReselect = null;
      }
    } catch (error) {
      console.error('[Tabs] loadByPolygon error:', error);
      this.renderEmpty(
        nlc_map_settings.generic_error_message || 'Something went wrong.'
      );
    }

    this.container.classList.remove('loading');
  }

  // Background loader: keeps fetching pages until totalSize is reached or the
  // polygon changes. Each page is merged and the tree is re-rendered.
  async streamRemainingTiles(geojsonPolygon, firstData, pageSize) {
    this._streamAbort = new AbortController();
    let offset = firstData.returnedSize;
    const total = firstData.totalSize;

    while (offset < total) {
      let page;
      try {
        page = await this.catalogs.getTilesByPolygon(geojsonPolygon, {
          signal: this._streamAbort.signal,
          limit: pageSize,
          offset,
        });
      } catch (e) {
        break;
      }
      if (!page || !Array.isArray(page.groups)) break;
      if (this._lastPolygon !== geojsonPolygon) break;

      this._lastData = this.mergeTileData(this._lastData, page);
      offset += page.returnedSize || 0;

      // While a search is active, full re-render is needed so filtering is
      // re-applied. Otherwise, append only the new tiles — keeps DOM stable,
      // scroll position untouched, and the user doesn't notice the update.
      if (this.currentSearch && this.currentSearch.length >= 2) {
        this.applyCurrentSearch();
      } else {
        const appended = this.appendIncrementalTiles(page);
        if (!appended) {
          // Fell back: the tree didn't exist yet, or new years arrived
          this.applyCurrentSearch();
        } else {
          this.updateStreamingBanner(this._lastData);
        }
      }

      if (!page.returnedSize) break;

      // Yield to the event loop so the browser can paint, handle clicks,
      // scroll events, and animations between pages — without this the
      // entire stream feels like one long blocking task.
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    this._streamAbort = null;
    this._streaming = false;
    this.updateStreamingBanner(this._lastData);
    // All pages landed → safe to drop overlays for tiles the user had
    // selected but that didn't show up in the new polygon's result.
    this.pruneObsoleteSelections();
    // Streaming finished; the carry-forward reselect set is no longer
    // needed (any tile that didn't arrive in any page is genuinely gone).
    this._pendingReselect = null;
  }

  // Insert new tiles from a streaming page into the existing DOM without
  // re-rendering the wrapper. Returns true on success, false if a full
  // re-render is needed (e.g. tree not built yet, or a new year appeared).
  appendIncrementalTiles(page) {
    if (!page || !Array.isArray(page.groups)) return false;
    const wrapper = this.container.querySelector('.js-nlc-maps-tabs-wrapper');
    if (!wrapper) return false;
    const tabsContainer = wrapper.querySelector('.nlc-maps-tabs__tabs');
    if (!tabsContainer) return false;

    const subcategoryTemplate = Handlebars.compile(
      document.getElementById('nlc-maps-subcategory-template').innerHTML
    );

    for (let gi = 0; gi < page.groups.length; gi++) {
      const group = page.groups[gi];
      const yearLabel = group.year != null ? String(group.year) : '—';
      const yearEl = tabsContainer.querySelector(
        `.nlc-maps-tabs__tab--group[data-year="${CSS.escape(yearLabel)}"]`
      );
      if (!yearEl) {
        // A new year appeared — fall back to full re-render so the tree gets it
        return false;
      }
      const yearSubtabs = yearEl.querySelector(':scope > .nlc-maps-tabs__sub-tabs');
      if (!yearSubtabs) return false;

      const cats = group.categories || [];
      for (let ci = 0; ci < cats.length; ci++) {
        const cat = cats[ci];
        const catName = cat.name || '—';
        const catEl = yearSubtabs.querySelector(
          `.nlc-maps-tabs__sub-tab--group[data-year="${CSS.escape(yearLabel)}"][data-cat="${CSS.escape(catName)}"]`
        );
        if (!catEl) return false; // new category — re-render
        const catSubtabs = catEl.querySelector(':scope > .nlc-maps-tabs__sub-tabs');
        if (!catSubtabs) return false;

        // Batch all new tile HTML into a single string and do ONE
        // insertAdjacentHTML per category — otherwise every tile would
        // trigger its own reflow, which is the main cause of the UI lag
        // during streaming.
        let batchHtml = '';
        (cat.tiles || []).forEach((tile) => {
          if (document.getElementById(tile.id)) return;
          batchHtml += subcategoryTemplate(this.tileToTemplateContext(tile));
        });
        if (batchHtml) {
          catSubtabs.insertAdjacentHTML('beforeend', batchHtml);
        }
      }
    }

    // Refresh year and category labels with the latest counts from _lastData.
    this.refreshGroupCounts();
    // Re-apply preview .active state to newly inserted tile buttons so visible
    // map previews keep showing as active in the freshly rendered list.
    this.restorePreviewActiveState();
    // If polygon-from-selection is in progress, re-check any newly-arrived
    // tile whose ID is in the carry-forward set. Without this, tiles that
    // land on pages 2+ would appear blank in the list even though the user
    // originally selected them.
    if (this._pendingReselect && this._pendingReselect.size > 0) {
      this._pendingReselect.forEach((id) => {
        const cb = document.getElementById(id);
        if (cb && cb.classList && cb.classList.contains('js-nlc-maps-checkbox') && !cb.checked) {
          cb.checked = true;
        }
      });
      const firstChecked = document.querySelector('.js-nlc-maps-checkbox:checked');
      if (firstChecked) this.syncGroupCheckboxes(firstChecked);
      this.updateSelectionPanel();
      this.refreshBulkSelectState();
    }
    return true;
  }

  // Mark each preview button as .active when there's an existing product
  // preview overlay on the map for that tile id. Used to keep the sidebar
  // in sync with map state after rendering (initial render, streamed page,
  // history restore).
  restorePreviewActiveState() {
    const previews =
      window.nlcMaps && window.nlcMaps.map && window.nlcMaps.map.productPreviews;
    if (!previews) return;
    Object.keys(previews).forEach((id) => {
      const btn = document.querySelector(
        `.js-nlc-maps-preview-btn[data-id="${CSS.escape(id)}"]`
      );
      if (btn) btn.classList.add('active');
    });
  }

  // Update the `(N)` count badge on every year and category header from
  // current _lastData. Called after incremental append.
  refreshGroupCounts() {
    if (!this._lastData || !Array.isArray(this._lastData.groups)) return;
    const wrapper = this.container.querySelector('.js-nlc-maps-tabs-wrapper');
    if (!wrapper) return;

    this._lastData.groups.forEach((group) => {
      const yearLabel = group.year != null ? String(group.year) : '—';
      const yearEl = wrapper.querySelector(
        `.nlc-maps-tabs__tab--group[data-year="${CSS.escape(yearLabel)}"]`
      );
      if (!yearEl) return;
      const yearTotal = (group.categories || []).reduce(
        (s, c) => s + ((c.tiles || []).length), 0
      );
      const yearLabelText = yearEl.querySelector(':scope > .nlc-maps-tabs__tab-header .nlc-maps-tabs__label-text');
      if (yearLabelText) {
        yearLabelText.innerHTML = `${this.escapeHtml(yearLabel)} <small>(${yearTotal})</small>`;
      }

      (group.categories || []).forEach((cat) => {
        const catName = cat.name || '—';
        const catEl = yearEl.querySelector(
          `.nlc-maps-tabs__sub-tab--group[data-year="${CSS.escape(yearLabel)}"][data-cat="${CSS.escape(catName)}"]`
        );
        if (!catEl) return;
        const catLabelText = catEl.querySelector(':scope > .nlc-maps-tabs__tab-header .nlc-maps-tabs__label-text');
        if (catLabelText) {
          catLabelText.innerHTML = `${this.escapeHtml(catName)} <small>(${(cat.tiles || []).length})</small>`;
        }
      });
    });
  }

  // Merge a new page of TilesByPolygon into an accumulated dataset.
  // Groups with the same year are unioned; categories with the same name
  // get their tiles concatenated (deduplicated by id).
  mergeTileData(acc, page) {
    if (!acc) return page;
    const groupByYear = new Map();
    (acc.groups || []).forEach((g) => groupByYear.set(g.year, g));

    (page.groups || []).forEach((g) => {
      const existing = groupByYear.get(g.year);
      if (!existing) {
        groupByYear.set(g.year, g);
        return;
      }
      const catByName = new Map();
      (existing.categories || []).forEach((c) => catByName.set(c.name, c));
      (g.categories || []).forEach((c) => {
        const existingCat = catByName.get(c.name);
        if (!existingCat) {
          existing.categories.push(c);
          return;
        }
        const seenIds = new Set(existingCat.tiles.map((t) => t.id));
        (c.tiles || []).forEach((t) => {
          if (!seenIds.has(t.id)) existingCat.tiles.push(t);
        });
      });
    });

    const merged = {
      ...acc,
      groups: Array.from(groupByYear.values()).sort(
        (a, b) => (b.year || 0) - (a.year || 0)
      ),
      returnedSize: (acc.returnedSize || 0) + (page.returnedSize || 0),
      totalSize: page.totalSize || acc.totalSize,
      truncated: ((acc.returnedSize || 0) + (page.returnedSize || 0)) < (page.totalSize || 0),
    };
    return merged;
  }

  // Render / update / remove the streaming / truncated banner as a SIBLING of
  // the tile wrapper. Keeping it out of the wrapper means the wrapper's height
  // and scroll position remain stable when the banner appears or disappears.
  updateStreamingBanner(data) {
    if (!this.container) return;
    const wrapper = this.container.querySelector('.js-nlc-maps-tabs-wrapper');
    if (!wrapper) return;

    let banner = this.container.querySelector('.js-nlc-maps-streaming-banner');

    const showStreaming = this._streaming && data && data.totalSize > data.returnedSize;
    const showTruncated = !showStreaming && data && data.truncated && data.totalSize > data.returnedSize;

    if (!showStreaming && !showTruncated) {
      if (banner) banner.remove();
      return;
    }

    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'js-nlc-maps-streaming-banner';
      wrapper.parentNode.insertBefore(banner, wrapper);
    }

    if (showStreaming) {
      banner.innerHTML = `
        <div class="nlc-maps-tabs__streaming" role="status" aria-live="polite">
          <span class="nlc-maps-tabs__streaming-spinner" aria-hidden="true"></span>
          <span class="nlc-maps-tabs__streaming-text">${this.escapeHtml(
            `Polygón je veľký, načítavam ďalšie dlaždice… ${data.returnedSize} z ${data.totalSize}`
          )}</span>
        </div>
      `;
    } else {
      banner.innerHTML = `
        <div class="nlc-maps-tabs__truncated">${this.escapeHtml(
          `Zobrazených ${data.returnedSize} z ${data.totalSize}. Zúžte oblasť pre úplný výsledok.`
        )}</div>
      `;
    }
  }

  // Show or hide the tile filter search input based on whether we have data.
  // Called whenever loaded data or state changes.
  updateSearchVisibility() {
    if (!this.container) return;
    const searchBar = this.container.querySelector('.js-nlc-maps-search');
    if (!searchBar) return;
    const hasTiles =
      this._lastData &&
      Array.isArray(this._lastData.groups) &&
      this._lastData.groups.length > 0;
    searchBar.style.display = hasTiles ? '' : 'none';
  }

  // Compute the bounding box of a GeoJSON Polygon and return a new Polygon
  // whose outer ring is the bbox rectangle. Used as a fallback when Apollo
  // rejects the simplified outline for self-intersection.
  polygonToBboxGeoJSON(geojsonPolygon) {
    const ring = geojsonPolygon && geojsonPolygon.coordinates && geojsonPolygon.coordinates[0];
    if (!Array.isArray(ring) || ring.length < 3) return null;

    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    ring.forEach(([lng, lat]) => {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    });
    if (!isFinite(minLng) || !isFinite(maxLng) || !isFinite(minLat) || !isFinite(maxLat)) {
      return null;
    }

    return {
      type: 'Polygon',
      coordinates: [[
        [minLng, minLat],
        [maxLng, minLat],
        [maxLng, maxLat],
        [minLng, maxLat],
        [minLng, minLat],
      ]],
    };
  }

  // Stop any background work (streaming pages, pending fetches) immediately.
  // Called when the user submits an order so we don't keep the server busy
  // on requests whose results will never be displayed.
  cancelInFlight() {
    if (this._streamAbort) {
      this._streamAbort.abort();
      this._streamAbort = null;
    }
    this._streaming = false;
  }

  // Show a transient loading indicator before the tile fetch even fires.
  // map.js calls this as soon as the user picks an area, so the user sees feedback
  // while the area's WKT is loaded and the new polygon is drawn.
  showLoading(message) {
    if (!this.container) return;
    const wrapper = this.ensureWrapper();
    const newWrapper = wrapper.cloneNode(false);
    const text = message ||
      nlc_map_settings.loading_tiles ||
      'Načítavam…';
    newWrapper.innerHTML = `
      <div class="nlc-maps-tabs__streaming" role="status" aria-live="polite">
        <span class="nlc-maps-tabs__streaming-spinner" aria-hidden="true"></span>
        <span class="nlc-maps-tabs__streaming-text">${this.escapeHtml(text)}</span>
      </div>
    `;
    wrapper.parentNode.replaceChild(newWrapper, wrapper);
    this.container.classList.add('loading');
  }

  // Reset tile list to the empty hint state (called when polygon is removed).
  clearTiles() {
    this._lastData = null;
    this.currentSearch = '';
    const searchInput = this.container && this.container.querySelector('.js-nlc-maps-product-search');
    if (searchInput) searchInput.value = '';
    this.updateSearchVisibility();
    const banner = this.container && this.container.querySelector('.js-nlc-maps-streaming-banner');
    if (banner) banner.remove();
    this.renderEmpty(
      nlc_map_settings.hint_draw_polygon_first ||
        nlc_map_settings.hint_draw_polygon ||
        'Draw a polygon on the map to load relevant tiles'
    );
  }

  // Re-render the currently loaded tile set after applying any active local search filter.
  // Called both after fresh load and after the user types in the search input.
  applyCurrentSearch() {
    this.updateSearchVisibility();
    if (!this._lastData) {
      this.renderEmpty(
        nlc_map_settings.hint_draw_polygon_first ||
          nlc_map_settings.hint_draw_polygon ||
          'Draw a polygon on the map to load relevant tiles'
      );
      return;
    }

    const query = this.normalizeSearch(this.currentSearch || '');
    if (query.length < 2) {
      this.renderGroups(this._lastData);
      return;
    }

    // Filter tiles locally — keep only those whose normalized title matches.
    // Normalization strips diacritics so "Nove" matches "Nové" / "Žilinský".
    const filtered = {
      ...this._lastData,
      groups: (this._lastData.groups || [])
        .map((group) => ({
          ...group,
          categories: (group.categories || [])
            .map((cat) => ({
              ...cat,
              tiles: (cat.tiles || []).filter((t) =>
                this.normalizeSearch(t.title || '').includes(query)
              ),
            }))
            .filter((cat) => cat.tiles.length > 0),
        }))
        .filter((group) => group.categories.length > 0),
    };

    this.renderGroups(filtered, { autoExpand: true });
    this.refreshBulkSelectState();
  }

  // Lowercase + strip diacritics so searching is accent-insensitive.
  // NFD decomposes "é" → "e" + combining acute, regex removes the combining mark.
  normalizeSearch(s) {
    return String(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();
  }

  // Render the year > category > tile hierarchy returned by TilesByPolygon.
  // When `autoExpand` is true (typically during search), all groups/categories
  // are rendered open so the user immediately sees the matching tiles.
  renderGroups(data, options = {}) {
    const wrapper = this.ensureWrapper();
    const groups = (data && data.groups) || [];

    if (groups.length === 0) {
      const msg =
        this.currentSearch && this.currentSearch.length >= 2
          ? (nlc_map_settings.no_products_found || 'No tiles match your search.')
          : (nlc_map_settings.no_products_found || 'No tiles found in this area.');
      this.renderEmpty(msg);
      return;
    }

    const subcategoryTemplate = Handlebars.compile(
      document.getElementById('nlc-maps-subcategory-template').innerHTML
    );

    let html = '';

    // Streaming / truncated indicator lives OUTSIDE the wrapper (as a sibling
    // element) so its appearance/disappearance during background loading does
    // not shift the wrapper's content — which would invalidate scrollTop.
    this.updateStreamingBanner(data);

    const chevronSvg = `
      <span class="nlc-maps-tabs__group-toggle">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </span>`;

    // Default CSS hides sub-tabs (display: none) — when we want them open
    // we must explicitly set display:block, otherwise CSS wins.
    const subtabsStyle = options.autoExpand
      ? 'style="display:block;"'
      : 'style="display:none;"';
    const activeClass = options.autoExpand ? ' active' : '';

    const expanded = options.autoExpand ? 'true' : 'false';
    let nextId = 0;
    const makeId = (prefix) => `nlc-tile-${prefix}-${++nextId}`;

    const groupCheckboxLabel = (text) =>
      this.escapeHtml(
        (nlc_map_settings.select_all || 'Vybrať všetko') + ' — ' + text
      );

    html += '<div class="nlc-maps-tabs__tabs" role="tree" aria-label="Tile categories">';
    groups.forEach((group) => {
      const yearLabel = group.year != null ? String(group.year) : '—';
      const yearTiles = (group.categories || []).reduce(
        (sum, c) => sum + ((c.tiles || []).length), 0
      );
      const yearPanelId = makeId('year-panel');
      const yearCbId = makeId('year-cb');

      html += `<div class="nlc-maps-tabs__tab nlc-maps-tabs__tab--group" role="treeitem" aria-expanded="${expanded}" data-year="${this.escapeAttr(yearLabel)}">`;
      html += `<div class="nlc-maps-tabs__tab-header js-nlc-maps-collapsible-header" role="button" tabindex="0" aria-controls="${yearPanelId}" aria-expanded="${expanded}" style="cursor:pointer;">`;
      // Group checkbox — selecting it ticks every tile under this year
      html += `<input type="checkbox" class="nlc-maps-tabs__checkbox js-nlc-maps-group-checkbox" id="${yearCbId}" aria-label="${groupCheckboxLabel(yearLabel)}" />`;
      html += `<span class="nlc-maps-tabs__label nlc-maps-tabs__label--root">`;
      html += `<span class="nlc-maps-tabs__label-text">${this.escapeHtml(yearLabel)} <small>(${yearTiles})</small></span>`;
      html += `</span>`;
      html += `<span class="nlc-maps-tabs__group-toggle${activeClass}" aria-hidden="true">`;
      html += `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>`;
      html += `</span>`;
      html += `</div>`;
      html += `<div class="nlc-maps-tabs__sub-tabs" id="${yearPanelId}" role="group" ${subtabsStyle}>`;
      (group.categories || []).forEach((cat) => {
        const catName = cat.name || '—';
        const tiles = cat.tiles || [];
        const catPanelId = makeId('cat-panel');
        const catCbId = makeId('cat-cb');

        html += `<div class="nlc-maps-tabs__sub-tab nlc-maps-tabs__sub-tab--group" role="treeitem" aria-expanded="${expanded}" data-year="${this.escapeAttr(yearLabel)}" data-cat="${this.escapeAttr(catName)}">`;
        html += `<div class="nlc-maps-tabs__tab-header js-nlc-maps-collapsible-header" role="button" tabindex="0" aria-controls="${catPanelId}" aria-expanded="${expanded}" style="cursor:pointer;">`;
        // Category-level group checkbox
        html += `<input type="checkbox" class="nlc-maps-tabs__checkbox js-nlc-maps-group-checkbox" id="${catCbId}" aria-label="${groupCheckboxLabel(catName)}" />`;
        html += `<span class="nlc-maps-tabs__label">`;
        html += `<span class="nlc-maps-tabs__label-text">${this.escapeHtml(catName)} <small>(${tiles.length})</small></span>`;
        html += `</span>`;
        html += `<span class="nlc-maps-tabs__group-toggle${activeClass}" aria-hidden="true">`;
        html += `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>`;
        html += `</span>`;
        html += `</div>`;
        html += `<div class="nlc-maps-tabs__sub-tabs" id="${catPanelId}" role="group" ${subtabsStyle}>`;
        tiles.forEach((tile) => {
          html += subcategoryTemplate(this.tileToTemplateContext(tile));
        });
        html += `</div>`;
        html += `</div>`;
      });
      html += `</div></div>`;
    });
    html += '</div>';

    // Announce result count for screen readers
    const total = (data && data.returnedSize != null) ? data.returnedSize :
      groups.reduce((s, g) => s + g.categories.reduce((c, cat) => c + cat.tiles.length, 0), 0);
    this.announce(`${total} tiles available across ${groups.length} year(s).`);

    // Preserve checked tile state across re-renders (happens during
    // background streaming and search filtering).
    const previouslyChecked = new Set(
      Array.from(wrapper.querySelectorAll('.js-nlc-maps-checkbox:checked')).map(
        (cb) => cb.id
      )
    );

    // Preserve which years / categories the user has expanded — otherwise
    // every streamed page would collapse the whole tree.
    const expandedYears = new Set();
    const expandedCats = new Set(); // key = year + '||' + cat name
    wrapper.querySelectorAll('.nlc-maps-tabs__tab--group').forEach((yearEl) => {
      const subtabs = yearEl.querySelector(':scope > .nlc-maps-tabs__sub-tabs');
      if (subtabs && subtabs.style.display === 'block') {
        const year = yearEl.getAttribute('data-year');
        if (year) expandedYears.add(year);
      }
    });
    wrapper.querySelectorAll('.nlc-maps-tabs__sub-tab--group').forEach((catEl) => {
      const subtabs = catEl.querySelector(':scope > .nlc-maps-tabs__sub-tabs');
      if (subtabs && subtabs.style.display === 'block') {
        const year = catEl.getAttribute('data-year');
        const cat = catEl.getAttribute('data-cat');
        if (year && cat) expandedCats.add(year + '||' + cat);
      }
    });

    // Preserve scroll position — streaming re-renders the wrapper every page,
    // which otherwise resets the user's scroll back to the top.
    const previousScrollTop = wrapper.scrollTop;

    const newWrapper = wrapper.cloneNode(false);
    newWrapper.innerHTML = html;
    wrapper.parentNode.replaceChild(newWrapper, wrapper);

    // Restore checked state on freshly rendered checkboxes — both the IDs
    // taken from the wrapper before this re-render AND any "carry forward"
    // IDs the polygon-from-selection flow asked us to keep ticked. Without
    // the carry-forward set, the convex-hull workflow looked like it forgot
    // the user's selections (chips emptied, checkboxes blank) even though
    // the on-map overlays were still showing.
    const restoreIds = new Set(previouslyChecked);
    if (this._pendingReselect) {
      this._pendingReselect.forEach((id) => restoreIds.add(id));
    }
    if (restoreIds.size > 0) {
      restoreIds.forEach((id) => {
        const cb = newWrapper.querySelector(`#${CSS.escape(id)}`);
        if (cb) cb.checked = true;
      });
      const firstChecked = newWrapper.querySelector('.js-nlc-maps-checkbox:checked');
      if (firstChecked) this.syncGroupCheckboxes(firstChecked);
    }

    // Re-expand the years / categories that were open BEFORE restoring
    // scrollTop. Otherwise the wrapper has collapsed content with smaller
    // scrollHeight and the browser clamps scrollTop down — visible as a jump.
    if (expandedYears.size > 0 || expandedCats.size > 0) {
      const expand = (containerEl) => {
        if (!containerEl) return;
        const sub = containerEl.querySelector(':scope > .nlc-maps-tabs__sub-tabs');
        const header = containerEl.querySelector(':scope > .nlc-maps-tabs__tab-header');
        if (sub) sub.style.display = 'block';
        if (header) {
          header.setAttribute('aria-expanded', 'true');
          const chev = header.querySelector('.nlc-maps-tabs__group-toggle');
          if (chev) chev.classList.add('active');
        }
        containerEl.setAttribute('aria-expanded', 'true');
      };
      expandedYears.forEach((year) => {
        const el = newWrapper.querySelector(
          `.nlc-maps-tabs__tab--group[data-year="${CSS.escape(year)}"]`
        );
        expand(el);
      });
      expandedCats.forEach((key) => {
        const [year, cat] = key.split('||');
        const el = newWrapper.querySelector(
          `.nlc-maps-tabs__sub-tab--group[data-year="${CSS.escape(year)}"][data-cat="${CSS.escape(cat)}"]`
        );
        expand(el);
      });
    }

    // Now apply scrollTop — scrollHeight has its final value at this point.
    if (previousScrollTop > 0) {
      newWrapper.scrollTop = previousScrollTop;
    }

    // Highlight matched search term in tile titles (visual cue for search hits)
    if (this.currentSearch && this.currentSearch.length >= 2) {
      this.highlightSearchMatches(newWrapper, this.currentSearch);
    }

    this.attachCollapsibleHeaderListeners(newWrapper);
    this.attachProductListeners(newWrapper);
    // Keep preview .active in sync with map.productPreviews after re-render.
    this.restorePreviewActiveState();

    // Refresh chip panel + bulk toolbar. Note: we intentionally do NOT prune
    // stale on-map overlays here — at this point we're often only looking at
    // page 1 of N streamed pages, and the user's previously-selected tiles
    // may still be on the way. Pruning happens once via pruneObsoleteSelections()
    // when streaming completes (or after a single-page load).
    this.updateSelectionPanel();
    this.refreshBulkSelectState();
  }

  // Clear every tile selection: untick checkboxes, drop on-map overlays,
  // refresh the selection panel + bulk toolbar. Called by the trash icon
  // (delete polygon) so the user starts fresh — without this the deleted
  // polygon was gone but the chips + map overlays from the old selection
  // lingered behind, contradicting the "everything reset" mental model.
  clearAllSelections() {
    const previouslyChecked = Array.from(
      document.querySelectorAll('.js-nlc-maps-checkbox:checked')
    );
    document
      .querySelectorAll(
        '.js-nlc-maps-checkbox:checked, .js-nlc-maps-group-checkbox:checked'
      )
      .forEach((cb) => {
        cb.checked = false;
        cb.indeterminate = false;
      });
    previouslyChecked.forEach((cb) => this._syncSelectionOverlayFor(cb));
    if (window.nlcMaps && window.nlcMaps.map) {
      window.nlcMaps.map.maybeEnableDisableOrderButton();
    }
    this.updateSelectionPanel();
    this.refreshBulkSelectState();
  }

  // Drop on-map overlays + chips for tiles the user had selected but that
  // didn't survive the latest polygon change. Runs ONCE per loadByPolygon
  // cycle — either right after the first render (no streaming) or after the
  // last streamed page lands. Until this fires, "missing" tiles are treated
  // as "not yet streamed in" so they don't get nuked prematurely.
  pruneObsoleteSelections() {
    if (!window.nlcMaps || !window.nlcMaps.map) return;
    const map = window.nlcMaps.map;
    const liveIds = new Set(
      Array.from(
        this.container.querySelectorAll('.js-nlc-maps-checkbox')
      ).map((cb) => cb.id)
    );
    if (!map.productPreviews) {
      this.updateSelectionPanel();
      return;
    }
    let removed = 0;
    Object.keys(map.productPreviews).forEach((id) => {
      if (!liveIds.has(id)) {
        const entry = map.productPreviews[id];
        if (entry.polygon) entry.polygon.setMap(null);
        if (entry.label) entry.label.setMap(null);
        delete map.productPreviews[id];
        removed++;
      }
    });
    if (removed > 0 && typeof map.updateTogglePreviewsButton === 'function') {
      map.updateTogglePreviewsButton();
    }
    if (typeof map.maybeEnableDisableOrderButton === 'function') {
      map.maybeEnableDisableOrderButton();
    }
    this.updateSelectionPanel();
    this.refreshBulkSelectState();
  }

  // Wrap occurrences of `query` (case + diacritic insensitive) inside tile
  // title text nodes with <mark>. Builds a map between original and normalized
  // characters so the highlight lines up on the rendered (accented) text.
  highlightSearchMatches(wrapper, query) {
    const queryNorm = this.normalizeSearch(query);
    if (queryNorm.length < 1) return;

    const titles = wrapper.querySelectorAll(
      '.nlc-maps-tabs__sub-tab .nlc-maps-tabs__label-text'
    );

    titles.forEach((el) => {
      const original = el.textContent || '';
      const { normalized, map } = this._normalizeWithMap(original);
      if (!normalized) return;

      const positions = [];
      let from = 0;
      while (true) {
        const idx = normalized.indexOf(queryNorm, from);
        if (idx < 0) break;
        const origStart = map[idx];
        const origEnd = map[idx + queryNorm.length - 1] + 1;
        positions.push([origStart, origEnd]);
        from = idx + queryNorm.length;
      }
      if (!positions.length) return;

      // Build escaped HTML with <mark> ranges in place
      let out = '';
      let cursor = 0;
      positions.forEach(([s, e]) => {
        out += this.escapeHtml(original.slice(cursor, s));
        out += '<mark>' + this.escapeHtml(original.slice(s, e)) + '</mark>';
        cursor = e;
      });
      out += this.escapeHtml(original.slice(cursor));
      el.innerHTML = out;
    });
  }

  // Produce a (normalized, originalIndexMap) pair. Iterates the source char
  // by char; per char, NFD-decompose, drop combining marks, lowercase the
  // remaining base character(s), and record which original index each
  // surviving normalized character came from.
  _normalizeWithMap(s) {
    const normalized = [];
    const map = [];
    for (let i = 0; i < s.length; i++) {
      const decomposed = s[i].normalize('NFD');
      for (const c of decomposed) {
        const code = c.codePointAt(0);
        if (code >= 0x0300 && code <= 0x036f) continue;
        normalized.push(c.toLowerCase());
        map.push(i);
      }
    }
    return { normalized: normalized.join(''), map };
  }

  // Coalesce multiple rapid checkbox changes into a single sync pass on the
  // next animation frame. Without this, every click during streaming pays
  // the cost of walking the entire ancestor chain + scanning all tile
  // checkboxes + rebuilding the selection panel — clicks feel laggy.
  scheduleCheckboxSync() {
    if (this._syncRafId) return;
    this._syncRafId = requestAnimationFrame(() => {
      this._syncRafId = null;
      const target = this._lastChangedCheckbox;
      this._lastChangedCheckbox = null;
      if (target && target.isConnected) {
        this.syncGroupCheckboxes(target);
      }
      if (window.nlcMaps && window.nlcMaps.map) {
        window.nlcMaps.map.maybeEnableDisableOrderButton();
      }
      this.updateSelectionPanel();
      this.refreshBulkSelectState();
    });
  }

  // Auto-mirror the cart selection onto the map: ticking a tile shows its
  // bounds as a filled solid overlay, unticking removes it. We grab bounds /
  // title from the sibling preview button in the same tile row (the data is
  // already there for the use-bounds button). After the per-tile button was
  // removed, the data-bounds / data-corners / data-title attributes live
  // directly on the checkbox itself — that's the source of truth now.
  _syncSelectionOverlayFor(tileCheckbox) {
    if (!tileCheckbox || !window.nlcMaps || !window.nlcMaps.map) return;
    const id = tileCheckbox.id;
    const title = tileCheckbox.getAttribute('data-title') || id;
    const cornersAttr = tileCheckbox.getAttribute('data-corners');
    const boundsAttr = tileCheckbox.getAttribute('data-bounds');
    if (!cornersAttr && !boundsAttr) return; // tile has no geometry
    let bounds = null;
    try {
      const parsedCorners = cornersAttr ? JSON.parse(cornersAttr) : null;
      if (Array.isArray(parsedCorners) && parsedCorners.length >= 3) {
        bounds = parsedCorners;
      } else if (boundsAttr) {
        bounds = JSON.parse(boundsAttr);
      }
    } catch (e) {
      bounds = null;
    }
    if (!bounds) return;

    const map = window.nlcMaps.map;
    const existing = map.productPreviews && map.productPreviews[id];

    if (tileCheckbox.checked) {
      // Need a 'selected' overlay. If none → create. If existing matches → noop.
      if (!existing) {
        map.toggleProductPreview(id, bounds, title, 'selected');
      } else if (existing.mode !== 'selected') {
        // Restyles in place — does NOT remove.
        map.toggleProductPreview(id, bounds, title, 'selected');
      }
    } else {
      // Unchecked → if we have an overlay tied to "selection", remove it.
      // (No-op: 'preview' overlays no longer come from anywhere — eye icon
      // was removed — so we always remove when unchecked.)
      if (existing) {
        map.toggleProductPreview(id, bounds, title, existing.mode || 'selected'); // toggles off
      }
    }
  }

  // Walk up the tile-tree from a child checkbox, recomputing each enclosing
  // group checkbox's state. Category → checked iff all its tiles are checked.
  // Year → checked iff all its tiles (across all its categories) are checked.
  // Sets indeterminate when only some descendants are checked.
  syncGroupCheckboxes(startEl) {
    let node = startEl.parentElement;
    while (node) {
      const isGroup =
        node.classList &&
        (node.classList.contains('nlc-maps-tabs__sub-tab--group') ||
          node.classList.contains('nlc-maps-tabs__tab--group'));
      if (isGroup) {
        const header = node.querySelector(':scope > .nlc-maps-tabs__tab-header');
        const groupCb = header && header.querySelector('.js-nlc-maps-group-checkbox');
        if (groupCb) {
          const tiles = Array.from(node.querySelectorAll('.js-nlc-maps-checkbox'));
          const allChecked = tiles.length > 0 && tiles.every((cb) => cb.checked);
          const someChecked = tiles.some((cb) => cb.checked);
          groupCb.checked = allChecked;
          groupCb.indeterminate = someChecked && !allChecked;
        }
      }
      node = node.parentElement;
    }
  }

  // Whole-row click (or Enter/Space) toggles year/category expand.
  // Ignores activations from interactive children so checkboxes / buttons keep
  // their native behaviour.
  attachCollapsibleHeaderListeners(wrapper) {
    const toggle = (header) => {
      const tab = header.closest('.nlc-maps-tabs__tab, .nlc-maps-tabs__sub-tab');
      if (!tab) return;
      const subTabs = tab.querySelector(':scope > .nlc-maps-tabs__sub-tabs');
      const chevron = header.querySelector('.nlc-maps-tabs__group-toggle');
      if (!subTabs) return;

      const isCollapsed =
        subTabs.style.display === 'none' || subTabs.style.display === '';
      subTabs.style.display = isCollapsed ? 'block' : 'none';
      header.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
      tab.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
      if (chevron) chevron.classList.toggle('active', isCollapsed);
    };

    wrapper.addEventListener('click', (event) => {
      if (event.target.closest('input, button, a, label')) return;
      const header = event.target.closest('.js-nlc-maps-collapsible-header');
      if (header) toggle(header);
    });

    wrapper.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const header = event.target.closest('.js-nlc-maps-collapsible-header');
      if (!header || event.target !== header) return;
      event.preventDefault();
      toggle(header);
    });
  }

  // Lazy aria-live region. Brief messages are read out by screen readers
  // without stealing focus or interrupting the user.
  announce(message) {
    if (!this._liveRegion) {
      const region = document.createElement('div');
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      region.style.cssText =
        'position:absolute;width:1px;height:1px;margin:-1px;border:0;padding:0;overflow:hidden;clip:rect(0 0 0 0);';
      document.body.appendChild(region);
      this._liveRegion = region;
    }
    // Clear first so identical consecutive messages still get announced
    this._liveRegion.textContent = '';
    setTimeout(() => {
      if (this._liveRegion) this._liveRegion.textContent = message;
    }, 50);
  }

  // Adapt a TilesByPolygon tile into the shape expected by the existing
  // subcategory Handlebars template (checkbox + preview/use-bounds buttons).
  tileToTemplateContext(tile) {
    const corners = Array.isArray(tile.wgs84Corners) ? tile.wgs84Corners : null;
    let bounds = null;
    if (corners && corners.length >= 3) {
      const xs = corners.map((c) => c.x);
      const ys = corners.map((c) => c.y);
      bounds = {
        west: Math.min(...xs),
        east: Math.max(...xs),
        south: Math.min(...ys),
        north: Math.max(...ys),
      };
    }
    return {
      id: tile.id,
      title: tile.title,
      hasData: true,
      wgs84Bounds: bounds,
      wgs84Corners: corners,
      // Glossary of abbreviation translations (computed server-side in PHP)
      glossary: Array.isArray(tile.glossary) ? tile.glossary : [],
    };
  }

  async browseTo(path) {
    this.container.classList.add('loading');
    this.mode = 'browse';

    try {
      const response = await this.catalogs.browsePath(path);

      if (response && response.success) {
        const data = response.data;
        this.currentPath = data.path || path;
        if (data.basePath) {
          this.basePath = data.basePath;
        }
        this.renderTree(data.subtrees || [], data.products || []);
        this.container.classList.remove('loading');
        return;
      }

      // Tree endpoint failed - fall back to flat list mode
      console.warn('[Tabs] Tree browsing unavailable, falling back to flat list');
      await this.loadPage(1);
    } catch (error) {
      console.error('[Tabs] Error browsing tree:', error);
      await this.loadPage(1);
    }

    this.container.classList.remove('loading');
  }

  async loadPage(page) {
    this.container.classList.add('loading');
    this.mode = 'search';

    try {
      let response;
      if (this.currentSearch.length >= 2) {
        response = await this.catalogs.search(this.currentSearch, page);
      } else {
        response = await this.catalogs.fetch(page);
      }

      if (response && response.success) {
        const data = response.data;
        const products = data.products || [];
        this.currentPage = data.page || page;
        this.totalPages = data.totalPages || 1;
        this.totalSize = data.totalSize || 0;
        this.pageSize = products.length;
        this.renderProducts(products);
      } else {
        this.renderProducts([]);
      }
    } catch (error) {
      console.error('[Tabs] Error loading products:', error);
      this.renderProducts([]);
    }

    this.container.classList.remove('loading');
  }

  registerHandlebars() {
    Handlebars.registerHelper('json', function (context) {
      return JSON.stringify(context);
    });

    Handlebars.registerPartial(
      'nlc-maps-subcategory-template',
      document.getElementById('nlc-maps-subcategory-template').innerHTML
    );

    Handlebars.registerPartial(
      'nlc-maps-category-template',
      document.getElementById('nlc-maps-category-template').innerHTML
    );
  }

  ensureWrapper() {
    let wrapper = this.container.querySelector('.js-nlc-maps-tabs-wrapper');
    if (!wrapper) {
      this.container.innerHTML = '';

      // Search starts hidden — only relevant once tiles are loaded.
      // updateSearchVisibility() flips it on/off as data state changes.
      const searchHtml = `
        <div class="nlc-maps-tabs__search js-nlc-maps-search" role="search" style="display:none;">
          <label for="nlc-maps-product-search" class="nlc-maps-tabs__search-label" style="position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);border:0;">${this.escapeHtml(this.getSearchPlaceholder())}</label>
          <input type="search" id="nlc-maps-product-search" class="nlc-maps-tabs__search-input js-nlc-maps-product-search"
                 placeholder="${this.escapeHtml(this.getSearchPlaceholder())}" value="${this.escapeHtml(this.currentSearch)}"
                 aria-label="${this.escapeHtml(this.getSearchPlaceholder())}">
          <span class="nlc-maps-tabs__search-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="M21 21l-4.35-4.35"></path>
            </svg>
          </span>
          <button type="button" class="nlc-maps-tabs__search-clear js-nlc-maps-product-search-clear" style="display:none;" aria-label="${this.escapeHtml(nlc_map_settings.clear_search || 'Clear search')}">&times;</button>
        </div>
      `;

      wrapper = document.createElement('div');
      wrapper.className = 'nlc-maps-tabs__wrapper js-nlc-maps-tabs-wrapper';

      this.container.insertAdjacentHTML('afterbegin', searchHtml);

      // Bulk-select toolbar — single button that toggles selection of every
      // tile currently shown in the list (after applying the search filter).
      // Hidden until tiles render.
      const bulkBar = document.createElement('div');
      bulkBar.className = 'nlc-maps-tabs__bulk-actions js-nlc-maps-bulk-actions';
      bulkBar.style.display = 'none';
      bulkBar.innerHTML = `
        <button type="button" class="nlc-maps-tabs__bulk-btn js-nlc-maps-select-visible">
          <span class="js-nlc-maps-bulk-label">Vybrať všetko zobrazené</span>
          <span class="js-nlc-maps-bulk-count"></span>
        </button>
      `;
      this.container.appendChild(bulkBar);

      this.container.appendChild(wrapper);
      this.initSearch();
      this.initBulkSelect(bulkBar);

      // Selection panel - insert between search and wrapper
      if (!this.selectionPanel) {
        const selectionDiv = document.createElement('div');
        selectionDiv.className = 'nlc-maps-tabs__selection js-nlc-maps-selection';
        selectionDiv.style.display = 'none';
        this.container.insertBefore(selectionDiv, wrapper);
        this.selectionPanel = selectionDiv;
        this.initSelectionPanel();
      }
    }
    return wrapper;
  }

  // Wire up the bulk-select toolbar:
  //   - Click toggles selection of currently-visible tile checkboxes (i.e.
  //     respects the search filter). If any are unchecked → check all.
  //     If all are already checked → uncheck all.
  //   - Visible count + label update on every render + every search change so
  //     "Vybrať všetko zobrazené (47)" always reflects reality.
  initBulkSelect(bar) {
    const selectBtn = bar.querySelector('.js-nlc-maps-select-visible');
    if (selectBtn) {
      selectBtn.addEventListener('click', (event) => {
        event.preventDefault();
        const visible = this._getVisibleTileCheckboxes();
        if (visible.length === 0) return;
        const allChecked = visible.every((cb) => cb.checked);
        const targetState = !allChecked;

        // Phase 1 — flip every checkbox synchronously. Cheap (no Maps work).
        const toSync = [];
        visible.forEach((cb) => {
          if (cb.checked !== targetState) {
            cb.checked = targetState;
            toSync.push(cb);
          }
        });
        if (visible.length > 0) {
          this._lastChangedCheckbox = visible[visible.length - 1];
        }
        this.scheduleCheckboxSync();
        this.refreshBulkSelectState();

        // Phase 2 — overlay sync in batches yielded via RAF. Selecting 2000
        // tiles synchronously locks the main thread for several seconds
        // because each setMap()/setOptions on Google Maps forces a redraw.
        // Yielding every 50 entries keeps the browser interactive: the
        // sidebar list paints, scroll works, clicks register — while
        // overlays appear progressively in the background.
        this._batchSyncOverlays(toSync);
      });
    }

    // Convex-hull-from-selection action — replaces the current polygon with
    // the "potato outline" of every selected tile. Confirms before clobbering
    // an existing polygon; Ctrl+Z restores it from the undo stack.
    const polyBtn = bar.querySelector('.js-nlc-maps-polygon-from-selection');
    if (polyBtn) {
      polyBtn.addEventListener('click', (event) => {
        event.preventDefault();
        if (polyBtn.disabled) return;
        if (!window.nlcMaps || !window.nlcMaps.map) return;
        const map = window.nlcMaps.map;

        const allCorners = this._collectSelectedTileCorners();
        if (allCorners.length < 3) return;

        if (map.currentPolygon) {
          const ok = window.confirm(
            'Nahradiť aktuálny polygón obkľučujúcim tvarom vybraných dlaždíc? Pôvodný polygón sa zruší (Ctrl+Z to vráti).'
          );
          if (!ok) return;
        }

        map.createPolygonFromCorners(allCorners);
      });
    }
  }

  // Apply _syncSelectionOverlayFor to a (potentially huge) list of checkboxes
  // in RAF-yielded batches so the main thread stays responsive even when the
  // user just bulk-selected 2000 tiles. Each batch creates ~50 Google Maps
  // overlays, then we hand control back to the browser for one paint frame.
  // After every checkbox is processed we refresh the panel + bulk toolbar
  // exactly once instead of after each yield.
  async _batchSyncOverlays(checkboxes) {
    if (!Array.isArray(checkboxes) || checkboxes.length === 0) return;
    const BATCH = 50;
    for (let i = 0; i < checkboxes.length; i += BATCH) {
      const end = Math.min(i + BATCH, checkboxes.length);
      for (let j = i; j < end; j++) {
        this._syncSelectionOverlayFor(checkboxes[j]);
      }
      if (end < checkboxes.length) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
    }
    this.updateSelectionPanel();
    this.refreshBulkSelectState();
  }

  // Iterate every CHECKED tile checkbox and return its geometry as a
  // LatLng[] path — used both for area summation (pricing) and convex-hull
  // polygon construction. Reads data-corners / data-bounds straight off the
  // checkbox, with productPreviews as a secondary source when the checkbox
  // is hidden (e.g., search filter removed it from view but selection stands).
  _iterateSelectedTilePaths() {
    const checked = Array.from(
      this.container.querySelectorAll('.js-nlc-maps-checkbox:checked')
    );
    const paths = [];
    checked.forEach((cb) => {
      let bounds = null;
      const cornersAttr = cb.getAttribute('data-corners');
      const boundsAttr = cb.getAttribute('data-bounds');
      try {
        const parsedCorners = cornersAttr ? JSON.parse(cornersAttr) : null;
        if (Array.isArray(parsedCorners) && parsedCorners.length >= 3) {
          bounds = parsedCorners;
        } else if (boundsAttr) {
          bounds = JSON.parse(boundsAttr);
        }
      } catch (e) {
        bounds = null;
      }
      // Fallback to overlay metadata when the checkbox's own data attrs are
      // missing (e.g., legacy DOM left over from a re-render edge case).
      if (!bounds) {
        const map = window.nlcMaps && window.nlcMaps.map;
        const overlay = map && map.productPreviews && map.productPreviews[cb.id];
        if (overlay && overlay.bounds) bounds = overlay.bounds;
      }
      if (!bounds) return;

      let path = [];
      if (Array.isArray(bounds) && bounds.length >= 3) {
        path = bounds.map((c) => new google.maps.LatLng(c.y, c.x));
      } else if (bounds && bounds.minX !== undefined) {
        path = [
          new google.maps.LatLng(bounds.minY, bounds.minX),
          new google.maps.LatLng(bounds.minY, bounds.maxX),
          new google.maps.LatLng(bounds.maxY, bounds.maxX),
          new google.maps.LatLng(bounds.maxY, bounds.minX),
        ];
      } else if (bounds && bounds.x !== undefined) {
        const minX = bounds.x, minY = bounds.y;
        const maxX = bounds.x + bounds.width, maxY = bounds.y + bounds.height;
        path = [
          new google.maps.LatLng(minY, minX),
          new google.maps.LatLng(minY, maxX),
          new google.maps.LatLng(maxY, maxX),
          new google.maps.LatLng(maxY, minX),
        ];
      }
      if (path.length >= 3) {
        paths.push({ id: cb.id, title: cb.getAttribute('data-title') || cb.id, path });
      }
    });
    return paths;
  }

  // Total billable area: SUM of every selected tile's km² — *not* the
  // polygon's area. Tiles often don't fully cover the polygon (user draws a
  // big area, picks a handful of tiles inside it), so charging by polygon
  // would overcharge. Returns { count, areaKm2, priceEur }.
  getSelectedTilesArea() {
    const paths = this._iterateSelectedTilePaths();
    let areaM2 = 0;
    paths.forEach((entry) => {
      areaM2 += google.maps.geometry.spherical.computeArea(entry.path);
    });
    const areaKm2 = areaM2 / 1000000;
    const pricePerKm2 = (typeof nlc_map_settings !== 'undefined'
      && nlc_map_settings && nlc_map_settings.price_per_km2 != null)
      ? Number(nlc_map_settings.price_per_km2)
      : 20;
    return {
      count: paths.length,
      areaKm2,
      priceEur: areaKm2 * pricePerKm2,
    };
  }

  // Pool every corner from every CHECKED tile checkbox into a flat array of
  // {x,y} points — input for the convex-hull polygon. We read corners off the
  // tile's own checkbox row via the sibling action button OR fall back to
  // bounds. Tiles without geometry are silently skipped.
  _collectSelectedTileCorners() {
    const corners = [];
    const checked = Array.from(
      this.container.querySelectorAll('.js-nlc-maps-checkbox:checked')
    );
    checked.forEach((cb) => {
      const row = cb.closest('.nlc-maps-tabs__sub-tab');
      if (!row) return;
      // Action button still carries data-corners / data-bounds even with the
      // per-tile button gone? — it was removed from the template, so we now
      // need the corners somewhere else. Use the previously-built overlay's
      // bounds (stored on map.productPreviews) as the source of truth.
      const id = cb.id;
      const map = window.nlcMaps && window.nlcMaps.map;
      const overlay = map && map.productPreviews && map.productPreviews[id];
      if (!overlay || !overlay.bounds) return;
      const b = overlay.bounds;
      if (Array.isArray(b) && b.length >= 3) {
        b.forEach((c) => corners.push({ x: c.x, y: c.y }));
      } else if (b && b.minX !== undefined) {
        corners.push({ x: b.minX, y: b.minY });
        corners.push({ x: b.maxX, y: b.minY });
        corners.push({ x: b.maxX, y: b.maxY });
        corners.push({ x: b.minX, y: b.maxY });
      } else if (b && b.x !== undefined) {
        const minX = b.x, minY = b.y, maxX = b.x + b.width, maxY = b.y + b.height;
        corners.push({ x: minX, y: minY });
        corners.push({ x: maxX, y: minY });
        corners.push({ x: maxX, y: maxY });
        corners.push({ x: minX, y: maxY });
      }
    });
    return corners;
  }

  // List of tile-row checkboxes that are currently visible in the sidebar.
  // "Visible" means CSS display !== 'none' on the row AND on every ancestor
  // sub-tabs panel — collapsed years/categories are excluded, so are tiles
  // hidden by the search filter.
  _getVisibleTileCheckboxes() {
    const all = Array.from(
      this.container.querySelectorAll('.js-nlc-maps-checkbox')
    );
    return all.filter((cb) => {
      let node = cb;
      while (node && node !== this.container) {
        if (node.nodeType === 1) {
          const style = window.getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
          }
        }
        node = node.parentNode;
      }
      return cb.offsetWidth > 0 || cb.offsetHeight > 0 || cb.getClientRects().length > 0;
    });
  }

  // Refresh the bulk-select toolbar's visible-count + button label so it
  // accurately mirrors how many tiles the click will affect, and whether the
  // current action is "select all" or "deselect all".
  refreshBulkSelectState() {
    const bar = this.container.querySelector('.js-nlc-maps-bulk-actions');
    if (!bar) return;
    const countEl = bar.querySelector('.js-nlc-maps-bulk-count');
    const labelEl = bar.querySelector('.js-nlc-maps-bulk-label');
    if (!countEl || !labelEl) return;

    const visible = this._getVisibleTileCheckboxes();
    if (visible.length === 0) {
      bar.style.display = 'none';
      return;
    }
    bar.style.display = '';
    const allChecked = visible.every((cb) => cb.checked);
    labelEl.textContent = allChecked
      ? 'Odznačiť všetko zobrazené'
      : 'Vybrať všetko zobrazené';
    countEl.textContent = '(' + visible.length + ')';

    // The "Vytvoriť polygón z vybraných" button lives in the selection
    // panel (updateSelectionPanel) — it's only relevant when there ARE
    // selections, which is exactly when that panel is visible.
  }

  renderTree(subtrees, products) {
    const wrapper = this.ensureWrapper();

    // Remove old pagination
    const existingPagination = this.container.querySelector('.js-nlc-maps-tabs-pagination');
    if (existingPagination) existingPagination.remove();

    let html = '';

    // Breadcrumb
    html += this.buildBreadcrumbHtml();

    // Subtrees (folders)
    if (subtrees.length > 0) {
      html += '<div class="nlc-maps-tabs__folders">';
      subtrees.forEach((name) => {
        const childPath = this.currentPath ? this.currentPath + '/' + name : '/' + name;
        html += `
          <div class="nlc-maps-tabs__folder js-nlc-maps-folder" data-path="${this.escapeAttr(childPath)}">
            <svg class="nlc-maps-tabs__folder-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
            <span class="nlc-maps-tabs__folder-name">${this.escapeHtml(name)}</span>
            <svg class="nlc-maps-tabs__folder-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
        `;
      });
      html += '</div>';
    }

    // Products
    if (products.length > 0) {
      const templateEl = document.getElementById('nlc-maps-tabs-template');
      const tabsTemplate = Handlebars.compile(templateEl.innerHTML);
      html += tabsTemplate({ categories: products });
    }

    // Empty state
    if (subtrees.length === 0 && products.length === 0) {
      const emptyMsg = nlc_map_settings.no_products_found || 'No products found.';
      html += `<div class="nlc-maps-tabs__empty">${emptyMsg}</div>`;
    }

    // Replace wrapper content
    const newWrapper = wrapper.cloneNode(false);
    newWrapper.innerHTML = html;
    wrapper.parentNode.replaceChild(newWrapper, wrapper);

    this.attachTreeListeners(newWrapper);
    this.attachProductListeners(newWrapper);
  }

  renderProducts(catalogs) {
    const wrapper = this.ensureWrapper();

    // Render products or empty message
    const templateEl = document.getElementById('nlc-maps-tabs-template');
    const tabsTemplate = Handlebars.compile(templateEl.innerHTML);

    let html = '';

    if (catalogs.length === 0 && this.currentSearch.length >= 2) {
      const noResultsMsg = nlc_map_settings.no_products_found || 'No products found.';
      html = `<div class="nlc-maps-tabs__empty">${noResultsMsg}</div>`;
    } else {
      html = tabsTemplate({ categories: catalogs });
    }

    // Replace wrapper
    const newWrapper = wrapper.cloneNode(false);
    newWrapper.innerHTML = html;
    wrapper.parentNode.replaceChild(newWrapper, wrapper);

    this.attachProductListeners(newWrapper);

    // During active search, open all locality groups so matching products are
    // visible and highlight the matched substring in their names.
    if (this.currentSearch && this.currentSearch.length >= 2) {
      this.expandAllGroups(newWrapper);
      this.highlightMatches(newWrapper, this.currentSearch);
    }

    // Pagination
    this.renderPagination();
  }

  // Open every collapsible group within `root` and rotate its chevron.
  expandAllGroups(root) {
    root.querySelectorAll('.nlc-maps-tabs__sub-tabs').forEach((el) => {
      el.style.display = 'block';
    });
    root.querySelectorAll('.nlc-maps-tabs__group-toggle').forEach((el) => {
      el.classList.add('active');
    });
  }

  // Wrap each case-insensitive occurrence of `query` inside label text nodes
  // with <mark class="nlc-maps-tabs__highlight">. Walks text nodes only so
  // existing markup (action buttons, info icons, etc.) is left intact.
  highlightMatches(root, query) {
    if (!query || query.length < 2) return;
    const re = new RegExp(this.escapeRegex(query), 'gi');
    const labelSelectors = [
      '.nlc-maps-tabs__display-name',
      '.nlc-maps-tabs__original-title',
      '.nlc-maps-tabs__label-text',
    ];
    const labels = root.querySelectorAll(labelSelectors.join(','));
    labels.forEach((el) => this.highlightTextNodes(el, re));
  }

  highlightTextNodes(el, re) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        // Skip text already inside a highlight
        if (node.parentNode && node.parentNode.classList
            && node.parentNode.classList.contains('nlc-maps-tabs__highlight')) {
          return NodeFilter.FILTER_REJECT;
        }
        return node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });
    const targets = [];
    let node;
    while ((node = walker.nextNode())) targets.push(node);

    targets.forEach((textNode) => {
      const text = textNode.nodeValue;
      re.lastIndex = 0;
      if (!re.test(text)) return;
      re.lastIndex = 0;
      const frag = document.createDocumentFragment();
      let last = 0;
      let match;
      while ((match = re.exec(text)) !== null) {
        if (match.index > last) {
          frag.appendChild(document.createTextNode(text.slice(last, match.index)));
        }
        const mark = document.createElement('mark');
        mark.className = 'nlc-maps-tabs__highlight';
        mark.textContent = match[0];
        frag.appendChild(mark);
        last = match.index + match[0].length;
        // Prevent infinite loop on zero-length matches
        if (re.lastIndex === match.index) re.lastIndex++;
      }
      if (last < text.length) {
        frag.appendChild(document.createTextNode(text.slice(last)));
      }
      textNode.parentNode.replaceChild(frag, textNode);
    });
  }

  escapeRegex(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  buildBreadcrumbHtml() {
    if (!this.currentPath) return '';

    // Strip basePath prefix from display (hides server IP, "archiv" etc.)
    const baseSegments = this.basePath ? this.basePath.split('/').filter(Boolean) : [];
    const allSegments = this.currentPath.split('/').filter(Boolean);
    const displaySegments = allSegments.slice(baseSegments.length);

    if (displaySegments.length === 0) return '';

    let html = '<nav class="nlc-maps-tabs__breadcrumb">';
    // Home icon navigates back to basePath (root of visible tree)
    html += `<span class="nlc-maps-tabs__breadcrumb-item js-nlc-maps-breadcrumb" data-path="${this.escapeAttr(this.basePath)}">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      </svg>
    </span>`;

    // Build path from basePath + displayed segments
    let pathSoFar = this.basePath || '';
    displaySegments.forEach((segment, index) => {
      pathSoFar += '/' + segment;
      html += '<span class="nlc-maps-tabs__breadcrumb-sep">/</span>';

      if (index === displaySegments.length - 1) {
        html += `<span class="nlc-maps-tabs__breadcrumb-item nlc-maps-tabs__breadcrumb-item--current">${this.escapeHtml(segment)}</span>`;
      } else {
        html += `<span class="nlc-maps-tabs__breadcrumb-item js-nlc-maps-breadcrumb" data-path="${this.escapeAttr(pathSoFar)}">${this.escapeHtml(segment)}</span>`;
      }
    });

    html += '</nav>';
    return html;
  }

  renderPagination() {
    const existing = this.container.querySelector('.js-nlc-maps-tabs-pagination');
    if (existing) existing.remove();

    if (this.totalPages <= 1) return;

    const ofLabel = nlc_map_settings.pagination_of || 'of';
    const totalLabel = nlc_map_settings.pagination_total || 'products';

    let options = '';
    for (let i = 1; i <= this.totalPages; i++) {
      options += `<option value="${i}" ${i === this.currentPage ? 'selected' : ''}>${i}</option>`;
    }

    const paginationHtml = `
      <div class="nlc-maps-tabs__pagination js-nlc-maps-tabs-pagination">
        <button type="button" class="nlc-maps-tabs__pagination-btn js-nlc-maps-page-prev" ${this.currentPage <= 1 ? 'disabled' : ''}>
          &laquo;
        </button>
        <span class="nlc-maps-tabs__pagination-info">
          <select class="nlc-maps-tabs__pagination-select js-nlc-maps-page-select">${options}</select>
          ${ofLabel} ${this.totalPages}
        </span>
        <button type="button" class="nlc-maps-tabs__pagination-btn js-nlc-maps-page-next" ${this.currentPage >= this.totalPages ? 'disabled' : ''}>
          &raquo;
        </button>
        <span class="nlc-maps-tabs__pagination-total">${this.pageSize} ${ofLabel} ${this.totalSize} ${totalLabel}</span>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', paginationHtml);

    const prevBtn = this.container.querySelector('.js-nlc-maps-page-prev');
    const nextBtn = this.container.querySelector('.js-nlc-maps-page-next');
    const pageSelect = this.container.querySelector('.js-nlc-maps-page-select');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (this.currentPage > 1) {
          this.loadPage(this.currentPage - 1);
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (this.currentPage < this.totalPages) {
          this.loadPage(this.currentPage + 1);
        }
      });
    }

    if (pageSelect) {
      pageSelect.addEventListener('change', () => {
        const page = parseInt(pageSelect.value, 10);
        if (page >= 1 && page <= this.totalPages) {
          this.loadPage(page);
        }
      });
    }
  }

  initSearch() {
    const searchInput = this.container.querySelector('.js-nlc-maps-product-search');
    const clearBtn = this.container.querySelector('.js-nlc-maps-product-search-clear');
    const searchIcon = this.container.querySelector('.nlc-maps-tabs__search-icon');

    if (!searchInput) return;

    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();

      if (clearBtn) {
        clearBtn.style.display = query.length > 0 ? 'flex' : 'none';
      }
      if (searchIcon) {
        searchIcon.style.display = query.length > 0 ? 'none' : 'flex';
      }

      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentSearch = query;
        this.applyCurrentSearch();
      }, 250);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        searchInput.value = '';
        clearBtn.style.display = 'none';
        if (searchIcon) searchIcon.style.display = 'flex';
        this.currentSearch = '';
        this.applyCurrentSearch();
      });
    }
  }

  attachTreeListeners(wrapper) {
    // Folder clicks
    wrapper.addEventListener('click', (event) => {
      const folder = event.target.closest('.js-nlc-maps-folder');
      if (folder) {
        event.preventDefault();
        const path = folder.getAttribute('data-path');
        this.browseTo(path);
      }
    });

    // Breadcrumb clicks
    wrapper.addEventListener('click', (event) => {
      const breadcrumb = event.target.closest('.js-nlc-maps-breadcrumb');
      if (breadcrumb) {
        event.preventDefault();
        const path = breadcrumb.getAttribute('data-path');
        this.browseTo(path);
      }
    });
  }

  attachProductListeners(wrapper) {
    // Toggle buttons (chevron toggle for all categories/groups)
    wrapper.addEventListener('click', (event) => {
      const groupToggle = event.target.closest('.js-nlc-maps-group-toggle');
      if (groupToggle) {
        const tab = groupToggle.closest('.nlc-maps-tabs__tab, .nlc-maps-tabs__sub-tab');
        if (tab) {
          const subcategories = tab.querySelector('.nlc-maps-tabs__sub-tabs');
          if (subcategories) {
            if (subcategories.style.display === 'block') {
              subcategories.style.display = 'none';
              groupToggle.classList.remove('active');
            } else {
              subcategories.style.display = 'block';
              groupToggle.classList.add('active');
            }
          }
        }
      }
    });

    // Checkbox changes — DOM mutations stay sync (cascade descendants for
    // group checkboxes), but the heavier work (walk up parents, refresh
    // selection panel, update order button) is coalesced into one
    // requestAnimationFrame so rapid clicks during streaming feel instant.
    wrapper.addEventListener('change', (event) => {
      if (event.target.classList.contains('js-nlc-maps-group-checkbox')) {
        const scope = event.target.closest(
          '.nlc-maps-tabs__sub-tab--group, .nlc-maps-tabs__tab--group, .nlc-maps-tabs__tab'
        );
        const touchedTileCheckboxes = [];
        if (scope) {
          const descendants = scope.querySelectorAll(
            '.js-nlc-maps-checkbox, .js-nlc-maps-group-checkbox'
          );
          descendants.forEach((cb) => {
            if (cb !== event.target) {
              cb.checked = event.target.checked;
              cb.indeterminate = false;
              if (cb.classList.contains('js-nlc-maps-checkbox')) {
                touchedTileCheckboxes.push(cb);
              }
            }
          });
        }
        this._lastChangedCheckbox = event.target;
        this.scheduleCheckboxSync();
        // Bulk apply the visual overlay diff so "Vybrať všetko" on a year /
        // category instantly mirrors on the map — same way an individual tile
        // tick does. Native click on a checkbox programmatically does NOT
        // re-fire `change`, so we walk the descendants ourselves.
        touchedTileCheckboxes.forEach((cb) => this._syncSelectionOverlayFor(cb));
        return;
      }

      if (event.target.classList.contains('js-nlc-maps-checkbox')) {
        this._lastChangedCheckbox = event.target;
        this.scheduleCheckboxSync();
        // Auto-sync the on-map overlay so the user immediately sees which
        // tile they just added (filled solid) — independent of the eye-icon
        // preview workflow. Without this, the checkbox only affected the
        // cart-side selection and people kept asking "did I add it or not?".
        this._syncSelectionOverlayFor(event.target);
      }
    });

    // Per-tile preview & use-bounds buttons are gone. Checkbox = the only way
    // to surface a tile on the map; the toolbar's "Vytvoriť polygón z
    // vybraných" handles "make my polygon match the selected tiles" for any
    // number of tiles. Info-button (glossary popover) is the only remaining
    // per-row click handler.
    wrapper.addEventListener('click', (event) => {
      const infoBtn = event.target.closest('.js-nlc-maps-info-btn');
      if (infoBtn) {
        event.preventDefault();
        event.stopPropagation();
        const glossary = JSON.parse(infoBtn.getAttribute('data-glossary'));
        this.showGlossaryPopover(infoBtn, glossary);
      }
    });
  }

  /**
   * Return precise bounds for a tile button.
   * In tree mode the API returns basic bounds, so we lazily fetch exact ones via TileTreeExactById.
   * In flat-list/search mode bounds from TileTreeExact are already precise — no extra call needed.
   */
  async getEnrichedBounds(button) {
    const id = button.getAttribute('data-id');
    const cornersAttr = button.getAttribute('data-corners');
    const boundsAttr = button.getAttribute('data-bounds');

    const parsedCorners = cornersAttr ? JSON.parse(cornersAttr) : null;
    const baseBounds = boundsAttr ? JSON.parse(boundsAttr) : null;

    // TilesByPolygon already ships precise corners on every tile — use them
    // directly and skip the lazy-cache path entirely. The cache used to leak
    // across polygons (same tile id from a different result mapped to stale
    // bounds), causing the preview button to zoom to a wrong location.
    if (Array.isArray(parsedCorners) && parsedCorners.length >= 3) {
      return parsedCorners;
    }

    // Legacy path — only kicks in if no corners were rendered.
    if (id && this.exactBoundsCache[id]) {
      return this.exactBoundsCache[id];
    }

    button.classList.add('loading');
    const exact = await this.catalogs.getExactBounds(id);
    button.classList.remove('loading');

    if (exact) {
      this.exactBoundsCache[id] = exact;
      if (Array.isArray(exact)) {
        button.setAttribute('data-corners', JSON.stringify(exact));
      } else {
        button.setAttribute('data-bounds', JSON.stringify(exact));
      }
      return exact;
    }

    return baseBounds;
  }

  showGlossaryPopover(targetBtn, glossary) {
    this.closeGlossaryPopover();

    const popover = document.createElement('div');
    popover.className = 'nlc-maps-glossary-popover';

    let entriesHtml = '';
    glossary.forEach((entry) => {
      entriesHtml += `
        <div class="nlc-maps-glossary-popover__entry">
          <div class="nlc-maps-glossary-popover__term">${this.escapeHtml(entry.term)}</div>
          <div class="nlc-maps-glossary-popover__translation">${this.escapeHtml(entry.translation)}</div>
        </div>
      `;
    });

    popover.innerHTML = `
      <button type="button" class="nlc-maps-glossary-popover__close js-nlc-maps-glossary-close">&times;</button>
      <div class="nlc-maps-glossary-popover__title">Vysvetlenie skratiek</div>
      ${entriesHtml}
    `;

    document.body.appendChild(popover);
    this.glossaryPopover = popover;

    const isMobile = window.innerWidth <= 767;

    if (isMobile) {
      // Center on screen with backdrop
      const backdrop = document.createElement('div');
      backdrop.className = 'nlc-maps-glossary-backdrop';
      document.body.appendChild(backdrop);
      this.glossaryBackdrop = backdrop;
      backdrop.addEventListener('click', () => this.closeGlossaryPopover());

      popover.classList.add('nlc-maps-glossary-popover--mobile');
    } else {
      // Position relative to button
      const rect = targetBtn.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      let left = rect.right + 8;
      let top = rect.top;

      if (left + popoverRect.width > window.innerWidth - 10) {
        left = rect.left - popoverRect.width - 8;
      }
      if (left < 10) {
        left = 10;
      }
      if (top + popoverRect.height > window.innerHeight - 10) {
        top = window.innerHeight - popoverRect.height - 10;
      }
      if (top < 10) top = 10;

      popover.style.left = left + 'px';
      popover.style.top = top + 'px';
    }

    // Close handlers
    popover.querySelector('.js-nlc-maps-glossary-close').addEventListener('click', () => {
      this.closeGlossaryPopover();
    });

    setTimeout(() => {
      this.glossaryClickOutsideHandler = (e) => {
        if (!popover.contains(e.target) && !targetBtn.contains(e.target)) {
          this.closeGlossaryPopover();
        }
      };
      document.addEventListener('click', this.glossaryClickOutsideHandler);
    }, 0);
  }

  closeGlossaryPopover() {
    if (this.glossaryPopover) {
      this.glossaryPopover.remove();
      this.glossaryPopover = null;
    }
    if (this.glossaryBackdrop) {
      this.glossaryBackdrop.remove();
      this.glossaryBackdrop = null;
    }
    if (this.glossaryClickOutsideHandler) {
      document.removeEventListener('click', this.glossaryClickOutsideHandler);
      this.glossaryClickOutsideHandler = null;
    }
  }

  getSearchPlaceholder() {
    return nlc_map_settings.search_placeholder || 'Search products...';
  }

  initSelectionPanel() {
    if (!this.selectionPanel) return;

    this.selectionPanel.addEventListener('click', (event) => {
      const btn = event.target.closest('button');
      if (!btn) return;

      // "Vytvoriť polygón z vybraných" — replace the current polygon with the
      // convex hull (potato outline) of every selected tile.
      if (btn.classList.contains('js-nlc-maps-polygon-from-selection')) {
        event.preventDefault();
        if (!window.nlcMaps || !window.nlcMaps.map) return;
        const map = window.nlcMaps.map;
        const allCorners = this._collectSelectedTileCorners();
        if (allCorners.length < 3) return;
        if (map.currentPolygon) {
          const ok = window.confirm(
            'Nahradiť aktuálny polygón obkľučujúcim tvarom vybraných dlaždíc? Pôvodný polygón sa zruší (Ctrl+Z to vráti).'
          );
          if (!ok) return;
        }
        // Remember which tiles the user had selected — the upcoming polygon
        // change triggers a fresh tile fetch and renderGroups may have a
        // narrow first-page window that misses some of these IDs. The
        // pending set is consulted in renderGroups / appendIncrementalTiles
        // and is cleared once streaming completes, so any tile from the
        // original selection lands re-checked the moment its row arrives.
        this._pendingReselect = new Set(
          Array.from(
            document.querySelectorAll('.js-nlc-maps-checkbox:checked')
          ).map((cb) => cb.id)
        );
        map.createPolygonFromCorners(allCorners);
        return;
      }

      // Remove product
      if (btn.hasAttribute('data-id')) {
        const id = btn.getAttribute('data-id');
        const checkbox = document.getElementById(id);
        if (checkbox) {
          checkbox.checked = false;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.updateSelectionPanel();
        return;
      }

      // Clear all
      if (btn.classList.contains('js-nlc-maps-selection-clear')) {
        const previouslyChecked = Array.from(
          document.querySelectorAll('.js-nlc-maps-checkbox:checked')
        );
        document.querySelectorAll('.js-nlc-maps-checkbox:checked, .js-nlc-maps-group-checkbox:checked').forEach((cb) => {
          cb.checked = false;
          cb.indeterminate = false;
        });
        // Without this loop the on-map overlays stayed visible even after
        // every checkbox in the sidebar was unchecked — clicking "Zrušiť
        // všetko" looked like a no-op on the map. _syncSelectionOverlayFor
        // sees `cb.checked === false` for each previously-selected tile and
        // tears down its dashed-blue overlay.
        previouslyChecked.forEach((cb) => this._syncSelectionOverlayFor(cb));
        if (window.nlcMaps && window.nlcMaps.map) {
          window.nlcMaps.map.maybeEnableDisableOrderButton();
        }
        this.updateSelectionPanel();
        this.refreshBulkSelectState();
      }
    });
  }

  updateSelectionPanel() {
    const panel = this.selectionPanel;
    if (!panel) return;

    const checkedProducts = document.querySelectorAll('.js-nlc-maps-checkbox:checked');
    const selectedAreas = (window.nlcMaps && window.nlcMaps.map) ? window.nlcMaps.map.selectedAreas || [] : [];
    const totalCount = checkedProducts.length + selectedAreas.length;

    if (totalCount === 0) {
      panel.style.display = 'none';
      panel.innerHTML = '';
      return;
    }

    let html = `<div class="nlc-maps-tabs__selection-header">`;
    html += `<span>Vybrané (${totalCount})</span>`;
    html += `<button type="button" class="nlc-maps-tabs__selection-clear-btn js-nlc-maps-selection-clear">&times; Zrušiť všetko</button>`;
    html += `</div>`;
    html += `<div class="nlc-maps-tabs__selection-items">`;

    // Areas first
    selectedAreas.forEach((area) => {
      html += `<span class="nlc-maps-tabs__selection-tag nlc-maps-tabs__selection-tag--area">
        <span class="nlc-maps-tabs__selection-tag-text">${this.escapeHtml(area.name)}</span>
        <button type="button" class="nlc-maps-tabs__selection-remove js-nlc-maps-selection-remove-area" data-area-id="${area.id}">&times;</button>
      </span>`;
    });

    // Products — cap visible chips so the panel doesn't render thousands of
    // DOM nodes when the user bulk-selects huge result sets. Anything beyond
    // the cap is collapsed into a summary tag.
    const CHIP_CAP = 50;
    const productsToShow = checkedProducts.length > CHIP_CAP
      ? Array.from(checkedProducts).slice(0, CHIP_CAP)
      : checkedProducts;
    productsToShow.forEach((cb) => {
      const title = cb.getAttribute('data-title') || cb.id;
      html += `<span class="nlc-maps-tabs__selection-tag">
        <span class="nlc-maps-tabs__selection-tag-text">${this.escapeHtml(title)}</span>
        <button type="button" class="nlc-maps-tabs__selection-remove js-nlc-maps-selection-remove" data-id="${this.escapeAttr(cb.id)}">&times;</button>
      </span>`;
    });
    if (checkedProducts.length > CHIP_CAP) {
      const hidden = checkedProducts.length - CHIP_CAP;
      html += `<span class="nlc-maps-tabs__selection-tag nlc-maps-tabs__selection-tag--more">
        <span class="nlc-maps-tabs__selection-tag-text">+ ${hidden} ďalších</span>
      </span>`;
    }

    html += `</div>`;

    // Over-limit warning when the user has selected more tiles than allowed.
    // (Area + price moved to the cart — that's where the user expects to
    // see pricing breakdowns. Sidebar only flags the hard cap.)
    if (checkedProducts.length > 0) {
      const summary = this.getSelectedTilesArea();
      const maxTiles = (typeof nlc_map_settings !== 'undefined'
        && nlc_map_settings && nlc_map_settings.max_tiles != null)
        ? Number(nlc_map_settings.max_tiles)
        : 4;
      if (summary.count > maxTiles) {
        const warning = (typeof nlc_map_settings !== 'undefined'
          && nlc_map_settings && nlc_map_settings.hint_too_many_tiles)
          ? nlc_map_settings.hint_too_many_tiles
          : `Prekročili ste rozsah povolenej plochy. Max ${maxTiles} mapové listy.`;
        html += `<div class="nlc-maps-tabs__selection-warning">${this.escapeHtml(warning)}</div>`;
      }
    }

    // "Convert selected tiles into a polygon" — placed inside the selection
    // panel so it's always visible right next to the chips, in the exact
    // spot where the user is looking at their picks. Earlier we hid it in
    // the sticky bulk-actions toolbar at the very top of the sidebar, but
    // users kept scrolling right past it.
    if (checkedProducts.length > 0) {
      html += `<button type="button" class="nlc-maps-tabs__selection-action js-nlc-maps-polygon-from-selection">
        Vytvoriť polygón z vybraných (${checkedProducts.length})
      </button>`;
    }

    panel.innerHTML = html;
    panel.style.display = 'block';
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

export default Tabs;
